/**
 * Beamify Cart Guard
 *
 * Prevents a customer from checking out with a customization product in the cart whose
 * base product has been removed (the incident this exists for: a customer deleted the base
 * tumbler and checked out with only the engraving service). See
 * docs/Cart_Customization_Protection_Plan.md (beamify_3d_customizer repo) for the full plan.
 *
 * Detection: customization lines are identified by product_type === 'Personalización' (set
 * by the product-creation backend, never localized — see the plan §3.1). Lines created after
 * this guard shipped carry a hidden `_base_variant_id` property pairing them to their exact
 * base variant; older lines have no properties and fall back to a coarser "any other
 * product in the cart" rule.
 *
 * This script runs on every page (included from layout/theme.liquid) because checkout links
 * exist outside the cart page too (header mini-cart, "added to cart" popup, customer menu).
 */
(function () {
  'use strict';

  var strings = window.beamifyCartGuardStrings || {
    checkoutBlockedMessage: 'Your customization requires its base product. Add it back to continue with checkout.',
    deleteWarning: "This product is required for your customization. If you remove it, you won't be able to complete checkout unless you add it back.\n\nDo you want to continue anyway?",
    checkoutBlockedTitle: "You can't checkout yet"
  };

  var CUSTOMIZATION_PRODUCT_TYPE = 'Personalización';
  var CHECKOUT_SELECTOR = '[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]';

  var guardViolated = false;
  var lastCartItems = null; // cached /cart.js items, refreshed by refreshGuard()

  /**
   * Returns true if any customization line in `items` is missing its required base product.
   */
  function computeViolation(items) {
    var nonCustomizationVariantIds = {};
    var hasAnyNonCustomization = false;

    items.forEach(function (item) {
      if (item.product_type !== CUSTOMIZATION_PRODUCT_TYPE) {
        hasAnyNonCustomization = true;
        nonCustomizationVariantIds[String(item.variant_id)] = true;
      }
    });

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.product_type !== CUSTOMIZATION_PRODUCT_TYPE) continue;

      var props = item.properties || {};
      var baseVariantId = props._base_variant_id;

      if (baseVariantId) {
        // Precise rule: this exact base variant must still be in the cart as a
        // non-customization line.
        if (!nonCustomizationVariantIds[String(baseVariantId)]) return true;
      } else {
        // Legacy line (created before this guard shipped, no pairing property): coarse rule.
        if (!hasAnyNonCustomization) return true;
      }
    }

    return false;
  }

  function injectStyles() {
    if (document.getElementById('beamify-cart-guard-style')) return;
    var style = document.createElement('style');
    style.id = 'beamify-cart-guard-style';
    style.textContent =
      '.beamify-cart-guard-disabled{opacity:0.5!important;' +
      'pointer-events:none!important;cursor:not-allowed!important;}' +
      '.beamify-cart-guard-notice{background:#fdf0ef;color:#c0392b;' +
      'border:1px solid #f0c4c0;border-radius:6px;padding:12px 14px;' +
      'margin-bottom:14px;font-size:13px;line-height:1.4;text-align:left;}';
    document.head.appendChild(style);
  }

  function updateCheckoutControls() {
    var controls = document.querySelectorAll(CHECKOUT_SELECTOR);
    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (guardViolated) {
        el.classList.add('beamify-cart-guard-disabled');
        el.setAttribute('aria-disabled', 'true');
        el.setAttribute('title', strings.checkoutBlockedTitle);
      } else {
        el.classList.remove('beamify-cart-guard-disabled');
        el.removeAttribute('aria-disabled');
        if (el.getAttribute('title') === strings.checkoutBlockedTitle) el.removeAttribute('title');
      }
    }
  }

  /**
   * Show/hide the in-page notice on the cart page (#beamify-cart-guard-notice, stamped in
   * cartpage-design1.liquid / cartpage-design2.liquid, right above the checkout button).
   * Unlike the banner this replaced, this is normal in-flow page content — it never overlays
   * anything and only exists on /cart, so it doesn't follow the customer to other pages.
   * No-ops on any page where the element isn't present.
   */
  function updateCartPageNotice() {
    var notice = document.getElementById('beamify-cart-guard-notice');
    if (!notice) return;
    if (guardViolated) {
      notice.textContent = strings.checkoutBlockedMessage;
      notice.style.display = '';
    } else {
      notice.style.display = 'none';
    }
  }

  /** Re-fetch /cart.js and re-evaluate the guard. Safe to call as often as needed. */
  function refreshGuard() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        lastCartItems = cart.items || [];
        guardViolated = computeViolation(lastCartItems);
        updateCheckoutControls();
        updateCartPageNotice();
      })
      .catch(function (err) {
        console.warn('[BeamifyCartGuard] Failed to refresh cart state:', err);
      });
  }

  // Capture-phase click interception on every checkout entry point (cart page button, header
  // mini-cart link, "added to cart" popup links, customer menu link). Capture phase runs
  // before jQuery's bubble-phase delegated handlers, and stopImmediatePropagation() here
  // prevents the click from reaching Shopify's own checkout submission entirely. This is
  // belt-and-braces alongside the visual/pointer-events disable above, since the theme
  // re-renders parts of the DOM after AJAX calls and a freshly-rendered element wouldn't
  // carry the disabled class until the next refreshGuard() call. The alert() is the ONLY
  // interruptive feedback in this guard — deliberately reactive, shown only on an actual
  // checkout attempt, never as a standing page banner (that blocked site navigation and
  // showed up on unrelated pages — see the incident this replaced).
  document.addEventListener('click', function (evt) {
    if (!guardViolated) return;
    var target = evt.target.nodeType === 1 ? evt.target.closest(CHECKOUT_SELECTOR) : null;
    if (!target) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    window.alert(strings.checkoutBlockedMessage);
  }, true);

  // Delete-link interception on the cart page. Rows are stamped with data-beamify-row /
  // data-product-type / data-variant-id (see cartpage-design1.liquid /
  // cartpage-design2.liquid). Runs in capture phase BEFORE the theme's own AJAX delete
  // handler (cart-template.liquid's `[href$="quantity=0"]` click binding on body), so
  // cancelling here stops that handler from ever running.
  document.addEventListener('click', function (evt) {
    if (evt.target.nodeType !== 1) return;
    var link = evt.target.closest('[href$="quantity=0"]');
    if (!link || !lastCartItems) return;

    var row = link.closest('[data-beamify-row]');
    if (!row) return;

    var productType = row.getAttribute('data-product-type');
    var variantId = row.getAttribute('data-variant-id');
    if (productType === CUSTOMIZATION_PRODUCT_TYPE) return; // deleting a customization itself is always fine

    var wouldOrphan = false;
    for (var i = 0; i < lastCartItems.length; i++) {
      var item = lastCartItems[i];
      if (item.product_type !== CUSTOMIZATION_PRODUCT_TYPE) continue;

      var props = item.properties || {};
      if (props._base_variant_id) {
        if (String(props._base_variant_id) === String(variantId)) { wouldOrphan = true; break; }
      } else {
        var otherNonCustomExists = lastCartItems.some(function (it) {
          return it.product_type !== CUSTOMIZATION_PRODUCT_TYPE && String(it.variant_id) !== String(variantId);
        });
        if (!otherNonCustomExists) { wouldOrphan = true; break; }
      }
    }

    if (wouldOrphan && !window.confirm(strings.deleteWarning)) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
    }
    // If the customer proceeds anyway, let the theme's own AJAX handler run as normal —
    // refreshGuard() (triggered below via ajaxComplete) picks up the resulting orphan state.
  }, true);

  function init() {
    injectStyles();
    refreshGuard();

    // Re-evaluate after every cart-mutating AJAX call the theme makes (add/update/change/
    // clear all hit routes under /cart/). The theme uses jQuery for all of these and never
    // reloads the page for them.
    if (window.jQuery) {
      window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
        if (settings && settings.url && settings.url.indexOf('/cart/') !== -1) {
          refreshGuard();
        }
      });
    }

    // Re-evaluate on bfcache restore (back/forward navigation) — cart state may have
    // changed since this page was cached.
    window.addEventListener('pageshow', refreshGuard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
